import React, { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, FormControlLabel, IconButton, Typography,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { ModuleColumn } from '../../types';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { sanitizeVisibleFields } from '../../utils/tableColumnSettings';

interface TableColumnEditorProps {
  allColumns: ModuleColumn[];
  visibleFields: string[];
  defaultFields: string[];
  onSave: (fields: string[]) => void | Promise<void>;
  onReset?: () => void | Promise<void>;
  compact?: boolean;
  saving?: boolean;
}

export const TableColumnEditor: React.FC<TableColumnEditorProps> = ({
  allColumns,
  visibleFields,
  defaultFields,
  onSave,
  onReset,
  compact,
  saving,
}) => {
  const [draft, setDraft] = useState(visibleFields);

  useEffect(() => { setDraft(visibleFields); }, [visibleFields]);

  const hiddenCount = allColumns.length - draft.length;
  const isDirty = JSON.stringify(draft) !== JSON.stringify(visibleFields);

  const toggleField = (field: string) => {
    setDraft((prev) => {
      if (prev.includes(field)) {
        const next = prev.filter((f) => f !== field);
        return next.length ? next : prev;
      }
      const map = new Map(allColumns.map((c) => [c.field, c]));
      const ordered = [...prev, field];
      return sanitizeVisibleFields(allColumns, ordered.filter((f) => map.has(f)));
    });
  };

  const moveField = (index: number, dir: -1 | 1) => {
    const next = [...draft];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setDraft(next);
  };

  const orderedCols = draft
    .map((field) => allColumns.find((c) => c.field === field))
    .filter(Boolean) as ModuleColumn[];

  const hiddenCols = allColumns.filter((c) => !draft.includes(c.field));

  return (
    <Box>
      {!compact && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, p: 1.5,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
          border: `1px solid ${brandCss.green100}`,
        }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: brandCss.gradientPrimary, color: '#fff', boxShadow: brandCss.shadowButtonLg,
          }}>
            <ViewColumnIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={800} fontSize={13} color={brandColors.textDark}>
              Visible columns
            </Typography>
            <Typography fontSize={11.5} color="text.secondary">
              {draft.length} shown · {hiddenCount} hidden — drag order with arrows
            </Typography>
          </Box>
          {hiddenCount > 0 && (
            <Chip size="small" label={`${hiddenCount} hidden`} sx={{ bgcolor: '#fff', fontWeight: 700, fontSize: 10.5 }} />
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {orderedCols.map((col, index) => (
          <Box
            key={col.field}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5,
              borderRadius: `${RADIUS}px`,
              border: `1px solid ${brandColors.border}`,
              bgcolor: '#fff',
              transition: 'all 0.15s',
              '&:hover': { borderColor: brandCss.primary, bgcolor: brandCss.green50 },
            }}
          >
            <FormControlLabel
              sx={{ flex: 1, m: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked
                  onChange={() => toggleField(col.field)}
                  sx={{ '&.Mui-checked': { color: brandCss.primary } }}
                />
              }
              label={<Typography fontSize={12.5} fontWeight={600}>{col.header}</Typography>}
            />
            <IconButton size="small" disabled={index === 0} onClick={() => moveField(index, -1)}>
              <ArrowUpwardIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" disabled={index === orderedCols.length - 1} onClick={() => moveField(index, 1)}>
              <ArrowDownwardIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ))}

        {hiddenCols.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography fontSize={11} fontWeight={700} color="text.secondary" sx={{ mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Hidden columns
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {hiddenCols.map((col) => (
                <Chip
                  key={col.field}
                  label={col.header}
                  size="small"
                  variant="outlined"
                  onClick={() => toggleField(col.field)}
                  sx={{
                    cursor: 'pointer', fontWeight: 600, fontSize: 11,
                    '&:hover': { borderColor: brandCss.primary, bgcolor: brandCss.green50 },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
        {onReset && (
          <Button
            size="small"
            startIcon={<RestartAltIcon />}
            onClick={() => { setDraft(defaultFields); onReset(); }}
            disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Reset default
          </Button>
        )}
        <Button
          size="small"
          variant="contained"
          disabled={saving || (!isDirty && JSON.stringify(draft) === JSON.stringify(visibleFields))}
          onClick={() => onSave(draft)}
          sx={{
            textTransform: 'none', fontWeight: 700, borderRadius: 2,
            background: brandCss.gradientPrimary,
            boxShadow: brandCss.shadowButton,
          }}
        >
          {saving ? 'Saving…' : 'Apply columns'}
        </Button>
      </Box>
    </Box>
  );
};
